import React from 'react'
import PipelineWidget from './PipelineWidget'
import FundedWidget from './FundedWidget'
import OpenRevenueWidget from './OpenRevenueWidget'
import PaidWidget from './PaidWidget'
import SubmittedWidget from './SubmittedWidget'
import TaskOverviewWidget from './TaskOverviewWidget'
import WhatsNewWidget from './WhatsNewWidget'
import PipelineValueWidget from './PipelineValueWidget'
import LeadOverviewWidget from './LeadOverviewWidget'
import YearlyStatisticsWidget from './YearlyStatisticsWidget'
import AverageBpsWidget from './AverageBpsWidget'
import FeedbackWidget from './FeedbackWidget'
import LenderMetricsWidget from './LenderMetricsWidget'
import TimeZoneWidget from './TimeZoneWidget'
import { DesignTheme } from './FinancialWidgetDesignSelector'

interface WidgetProps {
  className?: string
  designTheme?: DesignTheme
  widgetId?: string
}

export interface WidgetType {
  id: string
  name: string
  description: string
  defaultWidth: number
  defaultHeight: number
  minWidth: number
  minHeight: number
  maxWidth?: number
  maxHeight?: number
  component: React.ComponentType<WidgetProps>
}

export const AVAILABLE_WIDGETS: WidgetType[] = [
  {
    id: 'pipeline',
    name: 'Pipeline',
    description: 'Deals in pipeline stage',
    defaultWidth: 1,
    defaultHeight: 1,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 2,
    component: PipelineWidget,
  },
  {
    id: 'funded',
    name: 'Funded',
    description: 'Funded deals',
    defaultWidth: 1,
    defaultHeight: 1,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 2,
    component: FundedWidget,
  },
  {
    id: 'open_revenue',
    name: 'Open Revenue',
    description: 'Deals with open revenue',
    defaultWidth: 1,
    defaultHeight: 1,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 2,
    component: OpenRevenueWidget,
  },
  {
    id: 'paid',
    name: 'Paid',
    description: 'Paid deals',
    defaultWidth: 1,
    defaultHeight: 1,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 2,
    component: PaidWidget,
  },
  {
    id: 'submitted',
    name: 'Submitted',
    description: 'Submitted deals',
    defaultWidth: 1,
    defaultHeight: 1,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 2,
    component: SubmittedWidget,
  },
  {
    id: 'pipeline_value',
    name: 'Pipeline Value',
    description: 'Total pipeline value and commission',
    defaultWidth: 1,
    defaultHeight: 2,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 3,
    maxHeight: 3,
    component: PipelineValueWidget,
  },
  {
    id: 'task_overview',
    name: 'Task Overview',
    description: 'Overview of tasks',
    defaultWidth: 1,
    defaultHeight: 1,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 2,
    component: TaskOverviewWidget,
  },
  {
    id: 'whats_new',
    name: "What's New",
    description: 'Latest announcements and updates',
    defaultWidth: 1,
    defaultHeight: 2,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 3,
    component: WhatsNewWidget,
  },
  {
    id: 'lead_overview',
    name: 'Lead Overview',
    description: 'Lead pipeline and activity summary',
    defaultWidth: 1,
    defaultHeight: 2,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 3,
    component: LeadOverviewWidget,
  },
  {
    id: 'yearly_statistics',
    name: 'Yearly Statistics',
    description: 'Year-over-year performance statistics',
    defaultWidth: 1,
    defaultHeight: 2,
    minWidth: 1,
    minHeight: 2,
    maxWidth: 3,
    maxHeight: 4,
    component: YearlyStatisticsWidget,
  },
  {
    id: 'average_bps',
    name: 'Average BPS',
    description: 'Average basis points on raw commission',
    defaultWidth: 1,
    defaultHeight: 1,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 2,
    component: AverageBpsWidget,
  },
  {
    id: 'feedback',
    name: 'Feedback',
    description: 'Submit feedback, enhancements, and comments',
    defaultWidth: 1,
    defaultHeight: 2,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 2,
    maxHeight: 3,
    component: FeedbackWidget,
  },
  {
    id: 'lender_metrics',
    name: 'Lender Metrics',
    description: 'Track lender distribution and dominance',
    defaultWidth: 1,
    defaultHeight: 1,
    minWidth: 1,
    minHeight: 1,
    maxWidth: 1,
    maxHeight: 1,
    component: LenderMetricsWidget,
  },
  {
    id: 'world_clock',
    name: 'World Clock',
    description: 'Track time in multiple time zones',
    defaultWidth: 1,
    defaultHeight: 2,
    minWidth: 1,
    minHeight: 2,
    maxWidth: 2,
    maxHeight: 3,
    component: TimeZoneWidget,
  },
]

export const getWidgetComponent = (widgetId: string): React.ComponentType<WidgetProps> | null => {
  const widget = AVAILABLE_WIDGETS.find(w => w.id === widgetId)
  return widget ? widget.component : null
}

export const getWidgetConfig = (widgetId: string): WidgetType | null => {
  return AVAILABLE_WIDGETS.find(w => w.id === widgetId) || null
}